import { getPortalPocketBaseUrl, getPortalPbCustomerJson, getPortalPbToken, portalAuthzSidecarExpected } from '../../constants';
import type { LexProof } from './lexProof';
import { hasLexProofData, isVerifiedLexProof } from './lexProof';
import type { AccountKind } from './portalCustomerAccess';
import { isBetaTester, parseAccountKind } from './portalCustomerAccess';
import {
  normalizeTelemetryEndpoint,
  shouldSkipPortalTelemetryPayload,
} from './portalTelemetrySkip';

export {
  isIncompleteTelemetryPayload,
  normalizeTelemetryEndpoint,
  shouldSkipPortalTelemetryPayload,
  shouldSkipPortalTelemetryWebhook,
} from './portalTelemetrySkip';

export interface PortalTelemetryPayload {
  endpoint: string;
  status: number;
  lexDiag?: string;
  norm_id?: string;
  snapshot_version?: string;
}

const DEDUPE_MS = 30_000;
const recentReports = new Map<string, number>();

let fetchInterceptorInstalled = false;
/** Firma-PB ohne Collection: nach erstem 404 keine weiteren POSTs (Network-Noise). */
let portalTelemetryCollectionMissing = false;

function telemetryDedupeKey(payload: PortalTelemetryPayload): string {
  return [
    payload.endpoint,
    String(payload.status),
    payload.lexDiag ?? '',
    payload.norm_id ?? '',
  ].join('|');
}

function shouldSkipDuplicate(key: string): boolean {
  const now = Date.now();
  const prev = recentReports.get(key);
  if (prev != null && now - prev < DEDUPE_MS) return true;
  recentReports.set(key, now);
  return false;
}

export async function sha256CustomerId(customerId: string): Promise<string> {
  const id = customerId.trim();
  if (!id) throw new Error('customerId fehlt');
  const enc = new TextEncoder().encode(id);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function readPortalCustomerAccountKind(): AccountKind {
  try {
    const raw = getPortalPbCustomerJson();
    if (!raw) return 'customer';
    const parsed = JSON.parse(raw) as { account_kind?: unknown };
    return parseAccountKind(parsed?.account_kind);
  } catch {
    return 'customer';
  }
}

export function readPortalCustomerId(): string | null {
  try {
    const raw = getPortalPbCustomerJson();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown };
    return typeof parsed.id === 'string' && parsed.id.trim() ? parsed.id.trim() : null;
  } catch {
    return null;
  }
}

/** LEXCORE-Absage / Quality-Gate-Reject (HTTP 200, aber keine verifizierte Norm). */
export function isLexQualityGateReject(proof?: LexProof | null): boolean {
  if (!hasLexProofData(proof)) return false;
  return !isVerifiedLexProof(proof);
}

function firstNormIdFromProof(proof?: LexProof | null): string | undefined {
  const hit = proof?.sources?.find((s) => s.norm_id?.trim());
  return hit?.norm_id?.trim() || undefined;
}

function lexDiagFromProof(proof?: LexProof | null): string | undefined {
  const diag = proof?.lex_diag?.trim() || proof?.quality_gate?.trim();
  return diag || undefined;
}

let portalTelemetryNativeFetch: typeof fetch | null = null;

/** POST portal_telemetry — nur beta_tester, fire-and-forget, keine PII. */
export async function reportPortalTelemetry(payload: PortalTelemetryPayload): Promise<void> {
  if (portalTelemetryCollectionMissing) return;
  // Labor/Thomas: Collection + AuthZ vorhanden. Firma-Demo-PB oft ohne portal_telemetry → 404.
  if (!portalAuthzSidecarExpected()) return;
  if (!isBetaTester(readPortalCustomerAccountKind())) return;

  const customerId = readPortalCustomerId();
  const token = getPortalPbToken();
  if (!customerId || !token) return;

  const normalized: PortalTelemetryPayload = {
    endpoint: normalizeTelemetryEndpoint(payload.endpoint),
    status: payload.status,
    ...(payload.lexDiag ? { lexDiag: payload.lexDiag.slice(0, 8000) } : {}),
    ...(payload.norm_id ? { norm_id: payload.norm_id.slice(0, 200) } : {}),
    ...(payload.snapshot_version
      ? { snapshot_version: payload.snapshot_version.slice(0, 100) }
      : {}),
  };

  if (shouldSkipPortalTelemetryPayload(normalized)) return;

  const dedupeKey = telemetryDedupeKey(normalized);
  if (shouldSkipDuplicate(dedupeKey)) return;

  try {
    const customer_id_hash = await sha256CustomerId(customerId);
    if (!customer_id_hash || customer_id_hash.length !== 64) return;
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    const doFetch = portalTelemetryNativeFetch || fetch;
    const res = await doFetch(`${pbBase}/api/collections/portal_telemetry/records`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        customer_id_hash,
        endpoint: normalized.endpoint,
        status: normalized.status,
        ...(normalized.lexDiag ? { lexDiag: normalized.lexDiag } : {}),
        ...(normalized.norm_id ? { norm_id: normalized.norm_id } : {}),
        ...(normalized.snapshot_version ? { snapshot_version: normalized.snapshot_version } : {}),
      }),
    });
    if (res.status === 404 || res.status === 422) {
      portalTelemetryCollectionMissing = true;
    }
  } catch {
    /* Telemetrie darf den Nutzerfluss nicht stören */
  }
}

export function reportArkoHttpError(endpoint: string, status: number): void {
  void reportPortalTelemetry({ endpoint, status });
}

export function reportArkoQualityGateReject(endpoint: string, proof?: LexProof | null): void {
  if (!isLexQualityGateReject(proof)) return;
  void reportPortalTelemetry({
    endpoint,
    status: 200,
    lexDiag: lexDiagFromProof(proof),
    norm_id: firstNormIdFromProof(proof),
    snapshot_version: proof?.snapshot_version?.trim() || undefined,
  });
}

/** Globaler fetch-Hook: HTTP ≥ 400 auf Portal-Webhooks (/webhook/). */
export function installPortalTelemetryFetchInterceptor(): void {
  if (fetchInterceptorInstalled || typeof window === 'undefined') return;
  // Firma/Demo ohne AuthZ-Sidecar: kein Interceptor — sonst steckt jeder 401-Stack
  // in portalTelemetry.ts (nativeFetch-Zeile), und PB-Telemetry wäre ohnehin aus.
  if (!portalAuthzSidecarExpected()) {
    fetchInterceptorInstalled = true;
    return;
  }
  fetchInterceptorInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  portalTelemetryNativeFetch = nativeFetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    let response: Response;
    try {
      response = await nativeFetch(input, init);
    } catch (err) {
      /* Abort / Netzwerk — kein Telemetry-Report (sonst Demo-Konsole-Noise). */
      throw err;
    }
    try {
      if (init?.signal?.aborted) return response;
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      const path = normalizeTelemetryEndpoint(url);
      if (!path.includes('/webhook/') || response.status < 400) return response;
      if (shouldSkipPortalTelemetryPayload({ endpoint: path, status: response.status })) {
        return response;
      }
      reportArkoHttpError(path, response.status);
    } catch {
      /* ignore */
    }
    return response;
  };
}
